import { useParams, useNavigate, useSearchParams } from "react-router";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Badge } from "../../components/shared/Badge";
import { StatCard } from "../../components/shared/StatCard";
import { UserDrawer } from "../../components/shared/UserDrawer";
import {
  usePlatformEnterprise, usePlatformEnterpriseUsers,
  useSuspendEnterprise, useReinstateEnterprise,
  useResetUserPasswordPlatform, useUnlockPlatformUser,
  usePlatformUser, usePlatformRoles, useApproveUser,
} from "@platform/hooks";
import { useToast } from "@nthamizh/ui";
import { useState } from "react";
import { rootDomain, enterpriseSubdomainUrl } from "../../lib/subdomain";

interface Enterprise {
  id: string; name: string; slug: string;
  status: "active" | "suspended" | "pending"; createdAt: string;
  allowedDomains: string[];
}

interface EnterpriseUser {
  id: string; name: string; email: string; jobTitle: string | null;
  isLocked: boolean; failedLoginAttempts: number;
  status: "active" | "pending" | "inactive";
  memberSince: string;
  roles: { roleId: string; roleName: string; roleKey: string; moduleKey: string | null }[];
}

interface Role { id: string; name: string; key: string; moduleKey: string | null }

const statusBadge = (status: string, isLocked: boolean) => {
  if (isLocked) return <Badge variant="red">Locked</Badge>;
  if (status === "active") return <Badge variant="green">Active</Badge>;
  if (status === "pending") return <Badge variant="amber">Pending</Badge>;
  return <Badge variant="gray">Inactive</Badge>;
};

export function PlatformEnterpriseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Map ?tab= param to initial tab index
  const TAB_MAP: Record<string, number> = { users: 0, roles: 1, access: 2, settings: 3 };
  const initialTab = TAB_MAP[searchParams.get("tab") ?? "users"] ?? 0;
  const [activeTab, setActiveTab] = useState(initialTab);

  const { data: enterprise, isLoading: entLoading } = usePlatformEnterprise(id ?? null);
  const { data: users = [], isLoading: usersLoading } = usePlatformEnterpriseUsers(id ?? null);
  const { data: roles = [] } = usePlatformRoles();
  const { data: selectedUserData } = usePlatformUser(selectedUserId);
  const suspend = useSuspendEnterprise();
  const reinstate = useReinstateEnterprise();
  const resetPw = useResetUserPasswordPlatform();
  const unlock = useUnlockPlatformUser();
  const approve = useApproveUser();

  const ent = enterprise as Enterprise | undefined;
  const typedUsers = users as EnterpriseUser[];
  const typedRoles = roles as unknown as Role[];

  const TABS = ["All", "Active", "Pending", "Inactive", "Locked"];
  const filtered = typedUsers.filter((u) => {
    if (activeTab === 1) return u.status === "active" && !u.isLocked;
    if (activeTab === 2) return u.status === "pending";
    if (activeTab === 3) return u.status === "inactive";
    if (activeTab === 4) return u.isLocked;
    return true;
  });
  const tabCounts = [
    typedUsers.length,
    typedUsers.filter((u) => u.status === "active" && !u.isLocked).length,
    typedUsers.filter((u) => u.status === "pending").length,
    typedUsers.filter((u) => u.status === "inactive").length,
    typedUsers.filter((u) => u.isLocked).length,
  ];

  if (entLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!ent) {
    return (
      <div className="p-8 text-center text-sm text-ds-text-muted">
        Enterprise not found.{" "}
        <button onClick={() => navigate("/platform/enterprises")} className="text-indigo-600 hover:underline">
          Back to enterprises
        </button>
      </div>
    );
  }

  const handleSuspend = async () => {
    try { await suspend.mutateAsync(ent.id); toast({ title: `${ent.name} suspended`, variant: "success" }); }
    catch { toast({ title: "Failed to suspend", variant: "error" }); }
  };

  const handleReinstate = async () => {
    try { await reinstate.mutateAsync(ent.id); toast({ title: `${ent.name} reinstated`, variant: "success" }); }
    catch { toast({ title: "Failed to reinstate", variant: "error" }); }
  };

  const openDrawer = (userId: string) => setSelectedUserId(userId);

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate("/platform/enterprises")}
        className="flex items-center gap-1.5 text-sm text-ds-text-secondary hover:text-ds-text transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to enterprises
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {ent.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="text-lg font-semibold text-ds-text">{ent.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-sm text-indigo-600">{ent.slug}.{rootDomain()}</span>
              <Badge variant={ent.status === "active" ? "green" : ent.status === "suspended" ? "amber" : "gray"}>
                {ent.status}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <a
            href={enterpriseSubdomainUrl(ent.slug, "/dashboard")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-medium"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open workspace
          </a>
          {ent.status === "active" ? (
            <button onClick={handleSuspend} disabled={suspend.isPending}
              className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-medium disabled:opacity-50">
              Suspend
            </button>
          ) : (
            <button onClick={handleReinstate} disabled={reinstate.isPending}
              className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-medium disabled:opacity-50">
              Reinstate
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Total users" value={typedUsers.length} />
        <StatCard label="Active" value={typedUsers.filter((u) => u.status === "active").length} deltaType="positive" />
        <StatCard label="Pending" value={typedUsers.filter((u) => u.status === "pending").length}
          deltaType={typedUsers.filter((u) => u.status === "pending").length > 0 ? "warning" : "neutral"} />
        <StatCard label="Locked" value={typedUsers.filter((u) => u.isLocked).length}
          deltaType={typedUsers.filter((u) => u.isLocked).length > 0 ? "negative" : "neutral"} />
      </div>

      {/* Allowed domains */}
      {ent.allowedDomains?.length > 0 && (
        <div className="bg-white border border-ds-border rounded-card shadow-card px-5 py-3.5 flex items-center gap-3">
          <span className="text-xs font-medium text-ds-text-secondary">Auto-approved domains:</span>
          <div className="flex flex-wrap gap-1.5">
            {ent.allowedDomains.map((d) => (
              <span key={d} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200">@{d}</span>
            ))}
          </div>
        </div>
      )}

      {/* Users */}
      <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ds-border">
          <h2 className="text-sm font-semibold text-ds-text">Enterprise users</h2>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-ds-border">
          {TABS.map((tab, i) => (
            <button key={tab} onClick={() => setActiveTab(i)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeTab === i ? "border-indigo-600 text-indigo-700" : "border-transparent text-ds-text-secondary hover:text-ds-text"}`}>
              {tab} ({tabCounts[i]})
            </button>
          ))}
        </div>

        {usersLoading ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">Loading users...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">No users in this category.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ds-border bg-ds-bg">
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">User</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Roles</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Status</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Member since</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-border">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-ds-bg/50 cursor-pointer" onClick={() => openDrawer(u.id)}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold flex-shrink-0">
                        {u.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-ds-text">{u.name}</p>
                        <p className="text-xs text-ds-text-muted">{u.email}</p>
                        {u.jobTitle && <p className="text-xs text-ds-text-muted">{u.jobTitle}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length > 0
                        ? u.roles.map((r) => <Badge key={r.roleId} variant="blue">{r.roleName}</Badge>)
                        : <span className="text-xs text-ds-text-muted">No roles</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">{statusBadge(u.status, u.isLocked)}</td>
                  <td className="px-5 py-3.5 text-xs text-ds-text-muted">
                    {new Date(u.memberSince).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3.5 text-right text-xs text-indigo-600 font-medium">
                    Manage →
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UserDrawer
        user={selectedUserData as unknown as Parameters<typeof UserDrawer>[0]["user"]}
        availableRoles={typedRoles.map((r) => ({ id: r.id, name: r.name, key: r.key, moduleKey: r.moduleKey }))}
        isOpen={!!selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onApprove={async (uid) => {
          await approve.mutateAsync(uid);
          toast({ title: "User approved", variant: "success" });
        }}
        onUnlock={async (uid) => {
          await unlock.mutateAsync(uid);
          toast({ title: "User unlocked", variant: "success" });
        }}
        onResetPassword={async (uid) => resetPw.mutateAsync(uid)}
      />
    </div>
  );
}
